import { jsx, jsxs } from "react/jsx-runtime";
import { Box, BUTTON_TYPES, IconArrowRight, Button, IconButton } from "@zillow/constellation";
import { event } from "@zillow/universal-analytics";
import { yieldCallback } from "@zillow/yield-callback";
import PropTypes from "prop-types";
import "react";
import styled from "styled-components";
import { getCmsInfoForPage } from "../../utils/clickstream/getCmsInfoForPage.esm.js";
import { usePageProps } from "../PagePropsProvider/PagePropsProvider.esm.js";
import UpsellCard from "./UpsellCard.esm.js";
import UpsellHeading from "./UpsellHeading.esm.js";
import { MEDIA_TABLET, BREAKPOINT_TABLET, MEDIA_DESKTOP, BREAKPOINT_DESKTOP } from "./constants.esm.js";
import { SizePropType, ColorPropType } from "./prop-types.esm.js";
const Upsell = ({
  body,
  button,
  className,
  color,
  editorMode,
  heading,
  id,
  image,
  legal,
  size,
  url
}) => {
  const pageProps = usePageProps();
  const hasNonXsArrowCta = button === "" && ["sm", "md", "lg"].includes(size);
  const hasImage = size === "md" && !!image;
  const ColoredButtonComponent = editorMode ? ColoredButtonEditor : ColoredButton;
  function onClick() {
    if (!editorMode) {
      event({
        // https://dataportal.zillowgroup.net/event-registry/2048
        envelope: {
          event_type_id: "2048",
          event_type_version_id: "1",
          event_template_id: "126",
          event_template_version_id: "1"
          // Master ID: 2284.2.126.1
        },
        clickstream_trigger: {
          trigger_type_nm: "interaction",
          trigger_location_nm: "cms",
          trigger_source_nm: "cms_upsell",
          trigger_object_nm: "no_trigger_object"
        },
        semantic: {
          semantic_event_nm: "cms_click"
        },
        cms_info: {
          block_id: id || "",
          block_type_cd: "upsell",
          block_displayed_txt: `${heading}
${button}`,
          block_target_url: url,
          block_target_window_txt: "",
          ...getCmsInfoForPage(pageProps)
        }
      });
    }
  }
  return /* @__PURE__ */ jsx(UpsellCard, { button, className, color, editorMode, heading, id, size, url, children: /* @__PURE__ */ jsxs(Box, { sx: {
    display: "flex",
    // To contain the arrow button container.
    position: "relative",
    ...size === "xs" && {
      flexDirection: "column",
      [MEDIA_DESKTOP]: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between"
      }
    }
  }, children: [
    /* @__PURE__ */ jsxs(Box, { sx: {
      // Constrain the body and legal paragraph width when
      // showing arrow.
      ...hasNonXsArrowCta && {
        paddingRight: "$spacing.sm",
        [MEDIA_TABLET]: {
          paddingRight: "$spacing.lg",
          ".Upsell__Body, .Upsell__Legal": {
            maxWidth: "250px"
          }
        }
      },
      ...hasImage && {
        paddingRight: "0",
        [MEDIA_DESKTOP]: {
          paddingRight: "$spacing.xxl"
        }
      }
    }, children: [
      /* @__PURE__ */ jsx(UpsellHeading, { size, children: heading }),
      ["sm", "md", "lg"].includes(size) && /* @__PURE__ */ jsx(Box, { className: "Upsell__Body", as: "p", sx: {
        // Did not use <Text> for parent as I can't get
        // anything with low line spacing but bigger
        // font size.
        color: "var(--text)",
        fontSize: "$fontSizes.bodySmall",
        lineHeight: "$lineHeights.finePrint",
        // Box gets default vertical margin, remove that.
        margin: hasNonXsArrowCta && !legal ? "0" : "0 0 $spacing.sm 0",
        [MEDIA_TABLET]: {
          fontSize: "$fontSizes.body",
          lineHeight: "$lineHeights.body"
        }
      }, children: body }),
      ["sm", "md", "lg"].includes(size) && !!legal && /* @__PURE__ */ jsx(Box, { className: "Upsell__Legal", as: "p", fontType: "bodySmall", marginBottom: hasNonXsArrowCta ? void 0 : "sm", sx: {
        color: "var(--text-legal)",
        fontSize: "$fontSizes.bodySmall",
        lineHeight: "$lineHeights.finePrint"
      }, children: legal }),
      ["sm", "md", "lg"].includes(size) && button !== "" && /* @__PURE__ */ jsx(
        ColoredButtonComponent,
        {
          className: "Upsell__Button",
          buttonType: BUTTON_TYPES.primary,
          editorMode,
          forwardedAs: editorMode ? "div" : "a",
          href: editorMode ? void 0 : url,
          onClick: editorMode ? void 0 : yieldCallback(onClick),
          marginTop: {
            sm: "xl",
            md: {
              default: "xl",
              [BREAKPOINT_TABLET]: "lg"
            },
            lg: {
              default: "xl",
              [BREAKPOINT_TABLET]: "lg"
            }
          }[size],
          children: button
        }
      )
    ] }),
    (size === "xs" || hasNonXsArrowCta) && /* @__PURE__ */ jsx(ColoredIconButton, { icon: /* @__PURE__ */ jsx(IconArrowRight, {}), title: button, href: url, forwardedAs: "a", onClick: yieldCallback(onClick), appearance: "circle", marginLeft: "auto", className: "Upsell__Button", marginTop: size === "xs" ? {
      default: "xl",
      [BREAKPOINT_TABLET]: "xxl",
      [BREAKPOINT_DESKTOP]: 0
    } : void 0 }),
    hasImage && /* @__PURE__ */ jsx(Box, { sx: {
      display: "none",
      [MEDIA_DESKTOP]: {
        display: "block"
      },
      width: "50%",
      // Without flex-none, this block isn't actually
      // being 50%.
      flex: "none",
      img: {
        display: "block",
        height: "100%",
        width: "100%",
        objectFit: "cover",
        borderRadius: "$radii.xl"
      }
    }, children: image })
  ] }) });
};
const ColoredButton = styled(Button).withConfig({
  displayName: "Upsell__ColoredButton",
  componentId: "sc-85xoz5-0"
})(["background-color:var(--button);border-color:var(--button);color:var(--button-text);&:visited{background-color:var(--button);border-color:var(--button);color:var(--button-text);}"]);
const ColoredButtonEditor = styled(ColoredButton).withConfig({
  displayName: "Upsell__ColoredButtonEditor",
  componentId: "sc-85xoz5-1"
})(["padding:6px 8px;border-radius:4px;display:inline-block;user-select:auto;"]);
const ColoredIconButton = styled(IconButton).withConfig({
  displayName: "Upsell__ColoredIconButton",
  componentId: "sc-85xoz5-2"
})(["background-color:var(--button);border-color:var(--button);color:var(--background);font-weight:normal;align-self:flex-end;&:visited{background-color:var(--button);border-color:var(--button);color:var(--button-text);}"]);
Upsell.propTypes = {
  button: PropTypes.string,
  className: PropTypes.string,
  color: ColorPropType.isRequired,
  heading: PropTypes.string.isRequired,
  id: PropTypes.string,
  // This will be `withPreload(Image)` passed in from cms-blocks.
  // Only required for "md" size.
  image: PropTypes.node,
  // Only shows on "lg", "md", and "sm".
  legal: PropTypes.string,
  size: SizePropType.isRequired,
  // Only shows on "lg", "md", and "sm".
  body: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  editorMode: PropTypes.bool
};
Upsell.defaultProps = {
  button: "Learn More",
  className: void 0,
  editorMode: false,
  id: void 0,
  image: null,
  legal: void 0
};
export {
  Upsell as default
};
